import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { login as apiLogin, register as apiRegister, getMe, logoutSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);

  // Check auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("hospice_token");
      if (token) {
        try {
          const userData = await getMe();
          setUser(userData);
        } catch {
          localStorage.removeItem("hospice_token");
          localStorage.removeItem("hospice_user");
        }
      }
      setLoading(false);
    };
    
    initAuth();
    
    // Listen for logout events
    const handleLogout = () => {
      setUser(null);
      setRequires2FA(false);
    };
    window.addEventListener("auth-logout", handleLogout);
    return () => window.removeEventListener("auth-logout", handleLogout);
  }, []);

  const login = async (email, password) => {
    const response = await apiLogin(email, password);
    localStorage.setItem("hospice_token", response.token);
    localStorage.setItem("hospice_user", JSON.stringify(response));
    
    if (response.requires_2fa) {
      setRequires2FA(true);
      // Don't set full user yet until 2FA is verified
      return { ...response, requires_2fa: true };
    }
    
    setUser(response);
    setRequires2FA(false);
    
    // Dispatch event to trigger theme sync
    window.dispatchEvent(new Event("auth-login"));
    
    return response;
  };

  const complete2FA = (userData) => {
    setUser(userData);
    setRequires2FA(false);
    localStorage.setItem("hospice_user", JSON.stringify(userData));
  };

  const register = async (email, password, name, role = "caregiver", securityQuestion = null, securityAnswer = null) => {
    const response = await apiRegister(email, password, name, role, securityQuestion, securityAnswer);
    localStorage.setItem("hospice_token", response.token);
    localStorage.setItem("hospice_user", JSON.stringify(response));
    setUser(response);
    return response;
  };

  const logout = async () => {
    try {
      await logoutSession();
    } catch (error) {
      // Continue with logout even if API call fails
    }
    localStorage.removeItem("hospice_token");
    localStorage.removeItem("hospice_user");
    localStorage.removeItem("hospice_session_id");
    setUser(null);
    setRequires2FA(false);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("hospice_token");
    if (token) {
      try {
        const userData = await getMe();
        setUser(userData);
        return userData;
      } catch (error) {
        console.error("Failed to refresh user:", error);
      }
    }
    return null;
  };

  // Compute isAuthenticated based on user state
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, register, logout, loading, requires2FA, complete2FA, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
